import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Briefcase, Gavel, DollarSign, MessageSquare, Search, LogOut, Menu, X, 
  AlertTriangle, Share2, Languages, Lightbulb, User, ShieldCheck, Scale, PlusCircle
} from 'lucide-react';
import LawDirectory from './LawDirectory';
import AiAssistant from './AiAssistant';
import CaseList from '../components/CaseList';
import CaseRegistration from '../components/CaseRegistration';
import GrievanceRedressal from '../components/GrievanceRedressal';
import UserManagement from '../components/UserManagement';
import UserManagementPortal from '../components/UserManagementPortal';
import ThinkTankResearcher from '../components/ThinkTankResearcher';
import ChatSystem from '../components/ChatSystem';
import FinanceManagement from '../components/FinanceManagement';
import ReferralManagement from '../components/ReferralManagement';
import {
  ClientPortal,
  PractitionerPortal,
  LawFirmPortal,
  JudgePortal,
  SuperUserAdminPortal,
  GovtEntityPortal
} from '../components/RolePortals';

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeCategoryMenu, setActiveCategoryMenu] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number } | null>(null);

  useEffect(() => {
    const handleGlobalClick = () => {
      setContextMenu(null);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // THEME (Dark/Light/Easy) STATE VIA ThemeContext
  const { themeMode, setThemeMode, colors } = useTheme();

  const toggleLanguage = () => {
    const nextLng = i18n.language === 'en' ? 'ur' : 'en';
    i18n.changeLanguage(nextLng);
  };

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  if (isLoading || !user) return <div>Loading...</div>;

  // Theme values mapped to ThemeContext colors
  const theme = {
    bg: colors.pageBg,
    cardBg: colors.cardBg,
    text: colors.textColor,
    textMuted: colors.textMutedColor,
    border: colors.borderThemeColor,
    sidebarBg: colors.navyDark
  };

  const socialLinkStyle: React.CSSProperties = {
    color: theme.textMuted,
    transition: 'color 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'directory':
        return <LawDirectory />;
      case 'ai':
        return <AiAssistant />;
      case 'cases':
        return <CaseList />;
      case 'register-case':
        return <CaseRegistration onSuccess={() => setActiveTab('cases')} />;
      case 'finance':
        return <FinanceManagement />;
      case 'chat':
        return <ChatSystem />;
      case 'referrals':
        return <ReferralManagement />;
      case 'grievances':
        return <GrievanceRedressal />;
      case 'users':
        return <UserManagementPortal />;
      case 'think-tank-workspace':
        return <ThinkTankResearcher />;
      case 'portal':
        switch (user.role) {
          case 'client':
            return <ClientPortal />;
          case 'practitioner':
            return <PractitionerPortal />;
          case 'law_firm':
            return <LawFirmPortal />;
          case 'judge':
          case 'district_judge':
          case 'honourable_justice':
            return <JudgePortal />;
          case 'govt_entity':
            return <GovtEntityPortal />;
          case 'superuser':
          case 'db_admin':
          case 'administrator':
            return <SuperUserAdminPortal />;
          default:
            return <div style={{ padding: '2rem', textAlign: 'center' }}>Custom Portal View: {user.role}</div>;
        }
      case 'overview':
      default:
        return (
          <>
            <header style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{t('welcome')}, {user.name}</h1>
                <div style={{ backgroundColor: theme.border, padding: '0.3rem 0.8rem', borderRadius: '9999px', display: 'inline-block', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                  Role: <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>{user.role.replace('_', ' ')}</span>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <button 
                  onClick={toggleLanguage}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', border: `1px solid ${colors.borderThemeColor}`, borderRadius: '6px', backgroundColor: colors.cardBg, color: colors.textColor, cursor: 'pointer', fontWeight: 'bold' }}
                >
                  <Languages size={18} /> {i18n.language === 'en' ? 'اردو' : 'English'}
                </button>
                
                {/* 3-WAY THEME CONTROLLER */}
                <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.15)', padding: '0.2rem', borderRadius: '8px', border: `1px solid ${colors.borderThemeColor}` }}>
                  <button 
                    onClick={() => setThemeMode('dark')}
                    style={{ backgroundColor: themeMode === 'dark' ? colors.goldPrimary : 'transparent', color: themeMode === 'dark' ? '#0f172a' : colors.textColor, border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
                  >
                    🌙 Dark
                  </button>
                  <button 
                    onClick={() => setThemeMode('light')}
                    style={{ backgroundColor: themeMode === 'light' ? colors.goldPrimary : 'transparent', color: themeMode === 'light' ? '#0f172a' : colors.textColor, border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
                  >
                    ☀️ Light
                  </button>
                  <button 
                    onClick={() => setThemeMode('easy')}
                    style={{ backgroundColor: themeMode === 'easy' ? '#10b981' : 'transparent', color: themeMode === 'easy' ? '#ffffff' : colors.textColor, border: 'none', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
                  >
                    👁️ Eye-Comfort
                  </button>
                </div>
              </div>
            </header>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
              <StatCard title={t('active_cases')} value="0" icon={<Briefcase color="#ecc94b" />} onClick={() => setActiveTab('cases')} themeCardBg={theme.cardBg} themeText={theme.text} />
              <StatCard title={t('pending_payments')} value="0" icon={<DollarSign color="#ecc94b" />} onClick={() => setActiveTab('finance')} themeCardBg={theme.cardBg} themeText={theme.text} />
              {(user.role === 'admin' || user.role === 'senior_partner' || user.role === 'administrator' || user.role === 'associate' || user.role === 'practitioner' || user.role === 'law_firm') && <StatCard title="Expert Requests" value="0" icon={<Lightbulb color="#ecc94b" />} onClick={() => setActiveTab('think-tank-workspace')} themeCardBg={theme.cardBg} themeText={theme.text} />}
              <StatCard title={t('ai_queries')} value="0" icon={<MessageSquare color="#ecc94b" />} onClick={() => setActiveTab('ai')} themeCardBg={theme.cardBg} themeText={theme.text} />
            </div>

            {/* WINDOWS RIGHT-CLICK STYLE CATEGORY DROPDOWN MENU SYSTEM */}
            <section style={{ 
              backgroundColor: theme.cardBg, 
              padding: '1.75rem', 
              borderRadius: '16px', 
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)', 
              border: `1px solid ${theme.border}`,
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.6rem', color: '#f59e0b' }}>
                  💻 Main Service Explorer (Windows Context Menu Navigation)
                </h3>
                <span style={{ fontSize: '0.75rem', color: theme.textMuted, backgroundColor: 'rgba(245,158,11,0.1)', padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid rgba(245,158,11,0.3)' }}>
                  Hover or Click Category Below
                </span>
              </div>

              {/* WINDOWS CONTEXT MENU STRIP */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                
                {/* CATEGORY 1: LEGAL RESEARCH & DRAFTING */}
                <div 
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setActiveCategoryMenu('research')}
                  onMouseLeave={() => setActiveCategoryMenu(null)}
                >
                  <button 
                    onClick={(e) => { e.stopPropagation(); setActiveCategoryMenu(activeCategoryMenu === 'research' ? null : 'research'); }}
                    style={{
                      backgroundColor: '#1e293b',
                      color: '#f8fafc',
                      border: activeCategoryMenu === 'research' ? '1px solid #f59e0b' : '1px solid #334155',
                      padding: '0.65rem 1.1rem',
                      borderRadius: '10px',
                      fontWeight: '700',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>📜 Legal Research & Drafting</span>
                    <span style={{ fontSize: '0.7rem', color: '#f59e0b' }}>{activeCategoryMenu === 'research' ? '▼' : '▶'}</span>
                  </button>

                  {/* WINDOWS RIGHT-CLICK DROPDOWN CONTAINER */}
                  <div style={{
                    display: activeCategoryMenu === 'research' ? 'block' : 'none',
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '6px',
                    width: '280px',
                    backgroundColor: 'rgba(15, 23, 42, 0.98)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(245, 158, 11, 0.5)',
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.8)',
                    padding: '6px',
                    zIndex: 99999
                  }}>
                    <div style={{ padding: '6px 12px', fontSize: '0.7rem', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '4px' }}>
                      Core AI Drafting Services
                    </div>
                    <div onClick={() => { navigate('/smart-legal-drafting'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#f59e0b', fontWeight: 'bold', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem', transition: 'background 0.15s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(245,158,11,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>🖋️</span> Smart Legal Drafting & Pleading
                    </div>
                    <div onClick={() => { navigate('/ai-assistant'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#818cf8', fontWeight: 'bold', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem', transition: 'background 0.15s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(129,140,248,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>🤖</span> AI Legal Assistant
                    </div>
                    <div onClick={() => { navigate('/cases/search'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#38bdf8', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem', transition: 'background 0.15s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(56,189,248,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>🔍</span> Supreme Court & High Court Precedents
                    </div>
                    <div onClick={() => { navigate('/cases/search?type=act'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#cbd5e1', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem', transition: 'background 0.15s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>📜</span> Federal & Provincial Acts Library
                    </div>
                  </div>
                </div>

                {/* CATEGORY 2: CASE MANAGEMENT & HEARINGS */}
                <div 
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setActiveCategoryMenu('cases')}
                  onMouseLeave={() => setActiveCategoryMenu(null)}
                >
                  <button 
                    onClick={(e) => { e.stopPropagation(); setActiveCategoryMenu(activeCategoryMenu === 'cases' ? null : 'cases'); }}
                    style={{
                      backgroundColor: '#1e293b',
                      color: '#f8fafc',
                      border: activeCategoryMenu === 'cases' ? '1px solid #38bdf8' : '1px solid #334155',
                      padding: '0.65rem 1.1rem',
                      borderRadius: '10px',
                      fontWeight: '700',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>📁 Case Management & Docket</span>
                    <span style={{ fontSize: '0.7rem', color: '#38bdf8' }}>{activeCategoryMenu === 'cases' ? '▼' : '▶'}</span>
                  </button>

                  <div style={{
                    display: activeCategoryMenu === 'cases' ? 'block' : 'none',
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '6px',
                    width: '280px',
                    backgroundColor: 'rgba(15, 23, 42, 0.98)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(56, 189, 248, 0.5)',
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.8)',
                    padding: '6px',
                    zIndex: 99999
                  }}>
                    <div style={{ padding: '6px 12px', fontSize: '0.7rem', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '4px' }}>
                      Docket Operations
                    </div>
                    <div onClick={() => { setActiveTab('cases'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#38bdf8', fontWeight: 'bold', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(56,189,248,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>📋</span> My Cases & Cause Lists
                    </div>
                    <div onClick={() => { setActiveTab('register-case'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#34d399', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(52,211,153,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>➕</span> Register New Case File
                    </div>
                    <div onClick={() => { setActiveTab('referrals'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#f59e0b', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(245,158,11,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>👥</span> Client Legal Referrals
                    </div>
                  </div>
                </div>

                {/* CATEGORY 3: MEDIA & LIVE STREAMING */}
                <div 
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setActiveCategoryMenu('media')}
                  onMouseLeave={() => setActiveCategoryMenu(null)}
                >
                  <button 
                    onClick={(e) => { e.stopPropagation(); setActiveCategoryMenu(activeCategoryMenu === 'media' ? null : 'media'); }}
                    style={{
                      backgroundColor: '#1e293b',
                      color: '#f8fafc',
                      border: activeCategoryMenu === 'media' ? '1px solid #f43f5e' : '1px solid #334155',
                      padding: '0.65rem 1.1rem',
                      borderRadius: '10px',
                      fontWeight: '700',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>🎥 Judicial Broadcasts & Media</span>
                    <span style={{ fontSize: '0.7rem', color: '#f43f5e' }}>{activeCategoryMenu === 'media' ? '▼' : '▶'}</span>
                  </button>

                  <div style={{
                    display: activeCategoryMenu === 'media' ? 'block' : 'none',
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '6px',
                    width: '280px',
                    backgroundColor: 'rgba(15, 23, 42, 0.98)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(244, 63, 94, 0.5)',
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.8)',
                    padding: '6px',
                    zIndex: 99999
                  }}>
                    <div style={{ padding: '6px 12px', fontSize: '0.7rem', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '4px' }}>
                      Broadcasting & Updates
                    </div>
                    <div onClick={() => { navigate('/conference'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#38bdf8', fontWeight: 'bold', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem', transition: 'background 0.15s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(56,189,248,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>📹</span> Elum Meet (Video Conference & PiP)
                    </div>
                    <div onClick={() => { navigate('/admin/upload?tab=stream'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#f43f5e', fontWeight: 'bold', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(244,63,94,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>📡</span> Court Live Video Streaming
                    </div>
                    <div onClick={() => { navigate('/admin/upload'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#fbbf24', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(251,191,36,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>📤</span> Admin Upload Portal & Scraper
                    </div>
                  </div>
                </div>

                {/* CATEGORY 4: ROLE PORTALS & SUBSCRIPTIONS */}
                <div 
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setActiveCategoryMenu('roles')}
                  onMouseLeave={() => setActiveCategoryMenu(null)}
                >
                  <button 
                    onClick={(e) => { e.stopPropagation(); setActiveCategoryMenu(activeCategoryMenu === 'roles' ? null : 'roles'); }}
                    style={{
                      backgroundColor: '#1e293b',
                      color: '#f8fafc',
                      border: activeCategoryMenu === 'roles' ? '1px solid #a855f7' : '1px solid #334155',
                      padding: '0.65rem 1.1rem',
                      borderRadius: '10px',
                      fontWeight: '700',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>🛡️ Role Portals & Settings</span>
                    <span style={{ fontSize: '0.7rem', color: '#a855f7' }}>{activeCategoryMenu === 'roles' ? '▼' : '▶'}</span>
                  </button>

                  <div style={{
                    display: activeCategoryMenu === 'roles' ? 'block' : 'none',
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '6px',
                    width: '280px',
                    backgroundColor: 'rgba(15, 23, 42, 0.98)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(168, 85, 247, 0.5)',
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.8)',
                    padding: '6px',
                    zIndex: 99999
                  }}>
                    <div style={{ padding: '6px 12px', fontSize: '0.7rem', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '4px' }}>
                      Account & Role Views
                    </div>
                    <div onClick={() => { setActiveTab('portal'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#a855f7', fontWeight: 'bold', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(168,85,247,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>⚖️</span> My Role Portal View
                    </div>
                    {(user.role === 'admin' || user.role === 'administrator' || user.role === 'superuser' || user.role === 'db_admin') && (
                      <div onClick={() => { setActiveTab('users'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#f59e0b', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(245,158,11,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <span>👤</span> User & Access Control
                      </div>
                    )}
                    <div onClick={() => { setActiveTab('finance'); setActiveCategoryMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#34d399', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(52,211,153,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <span>💳</span> Subscription & Finance Vault
                    </div>
                  </div>
                </div>

              </div>
            </section>
          </>
        );
    }
  };

  return (
    <div 
      onContextMenu={handleRightClick}
      style={{ display: 'flex', height: '100vh', backgroundColor: theme.bg, color: theme.text, direction: i18n.language === 'ur' ? 'rtl' : 'ltr', transition: 'all 0.3s ease', position: 'relative' }}
    >
      {/* WINDOWS 11 AUTHENTIC RIGHT-CLICK CONTEXT MENU OVERLAY */}
      {contextMenu && contextMenu.visible && (
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            width: '270px',
            backgroundColor: 'rgba(15, 23, 42, 0.98)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(245, 158, 11, 0.5)',
            borderRadius: '12px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.8), 0 0 15px rgba(245,158,11,0.2)',
            padding: '6px',
            zIndex: 999999,
            fontFamily: '"Outfit", sans-serif'
          }}
        >
          <div style={{ padding: '6px 12px', fontSize: '0.7rem', fontWeight: 'bold', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '4px' }}>
            💻 Elum Legal Windows Explorer
          </div>

          <div onClick={() => { navigate('/smart-legal-drafting'); setContextMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#f59e0b', fontWeight: 'bold', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(245,158,11,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <span>🖋️</span> Smart Legal Drafting & Pleading
          </div>

          <div onClick={() => { navigate('/ai-assistant'); setContextMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#818cf8', fontWeight: 'bold', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(129,140,248,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <span>🤖</span> AI Legal Assistant
          </div>

          <div onClick={() => { navigate('/cases/search'); setContextMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#38bdf8', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(56,189,248,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <span>🔍</span> Supreme Court & High Court Precedents
          </div>

          <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '4px 0' }} />

          <div onClick={() => { setActiveTab('cases'); setContextMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#cbd5e1', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <span>📋</span> My Cases & Cause Lists
          </div>

          <div onClick={() => { navigate('/admin/upload?tab=stream'); setContextMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#f43f5e', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(244,63,94,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <span>📡</span> Court Live Video Streaming
          </div>

          <div onClick={() => { setActiveTab('portal'); setContextMenu(null); }} style={{ padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#a855f7', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(168,85,247,0.15)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <span>⚖️</span> My Role Portal View
          </div>
        </div>
      )}
      {/* Sidebar */}
      <aside style={{ 
        width: sidebarOpen ? '250px' : '80px', 
        backgroundColor: theme.sidebarBg, 
        color: 'white', 
        transition: 'width 0.3s', 
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        flexShrink: 0 
      }}>
        <div style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          {sidebarOpen && <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Law Firm</h2>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
            {sidebarOpen ? <X /> : <Menu />}
          </button>
        </div>
        
        <nav style={{ 
          marginTop: '1rem', 
          display: 'flex', 
          flexDirection: 'column', 
          flex: 1, 
          overflowY: 'auto',
          scrollbarWidth: 'none'
        }}>
          {/* Main Page vertical button */}
          <SidebarItem icon={<Scale color="#ecc94b" />} label={i18n.language === 'ur' ? 'صفحہ اول' : 'Main Page'} active={false} onClick={() => navigate('/')} open={sidebarOpen} />
          
          {/* DASHBOARD HOME */}
          <SidebarItem icon={<Briefcase />} label="Dashboard Home" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} open={sidebarOpen} />

          {/* CLIENT PORTAL SIDEBAR NAVIGATION BAR (Client Role Only) */}
          {user.role === 'client' && (
            <>
              <SidebarItem icon={<MessageSquare color="#25D366" />} label={i18n.language === 'ur' ? 'ایلئم لائیو چیٹ' : 'Elum Live Chat'} active={activeTab === 'chat'} onClick={() => setActiveTab('chat')} open={sidebarOpen} />
              <SidebarItem icon={<Search color="#0284c7" />} label={t('law_directory')} active={activeTab === 'directory'} onClick={() => setActiveTab('directory')} open={sidebarOpen} />
              <SidebarItem icon={<MessageSquare color="#38bdf8" />} label={t('ai_assistant')} active={activeTab === 'ai'} onClick={() => setActiveTab('ai')} open={sidebarOpen} />
              <SidebarItem icon={<PlusCircle color="#48bb78" />} label={i18n.language === 'ur' ? 'کیس رجسٹریشن' : 'Case Registration'} active={activeTab === 'register-case'} onClick={() => setActiveTab('register-case')} open={sidebarOpen} />
              <SidebarItem icon={<Gavel color="#ecc94b" />} label={i18n.language === 'ur' ? 'کیس فالو اپ' : 'Follow Up & Cases'} active={activeTab === 'cases' || activeTab === 'portal'} onClick={() => setActiveTab('cases')} open={sidebarOpen} />
            </>
          )}

          {/* FULL DASHBOARD SIDEBAR MENU (All Other Roles Kept Completely Intact) */}
          {user.role !== 'client' && (
            <>
              {(user.role === 'admin' || user.role === 'senior_partner' || user.role === 'administrator' || user.role === 'superuser' || user.role === 'db_admin') && (
                <SidebarItem icon={<User />} label="Manage Users" active={activeTab === 'users'} onClick={() => setActiveTab('users')} open={sidebarOpen} />
              )}
              {(user.role === 'admin' || user.role === 'senior_partner' || user.role === 'administrator' || user.role === 'associate' || user.role === 'practitioner' || user.role === 'law_firm') && (
                <SidebarItem icon={<Lightbulb />} label="Think Tank" active={activeTab === 'think-tank-workspace'} onClick={() => setActiveTab('think-tank-workspace')} open={sidebarOpen} />
              )}
              <SidebarItem icon={<Gavel />} label="My Cases" active={activeTab === 'cases'} onClick={() => setActiveTab('cases')} open={sidebarOpen} />
              {(user.role === 'practitioner' || user.role === 'law_firm') && (
                <SidebarItem icon={<Share2 />} label="Referrals" active={activeTab === 'referrals'} onClick={() => setActiveTab('referrals')} open={sidebarOpen} />
              )}
              <SidebarItem icon={<Search />} label={t('law_directory')} active={activeTab === 'directory'} onClick={() => setActiveTab('directory')} open={sidebarOpen} />
              <SidebarItem icon={<MessageSquare />} label={t('ai_assistant')} active={activeTab === 'ai'} onClick={() => setActiveTab('ai')} open={sidebarOpen} />
              <SidebarItem icon={<MessageSquare />} label={i18n.language === 'ur' ? 'ایلئم لائیو چیٹ' : 'Elum Live Chat'} active={activeTab === 'chat'} onClick={() => setActiveTab('chat')} open={sidebarOpen} />
              <SidebarItem icon={<ShieldCheck />} label="My Role Portal" active={activeTab === 'portal'} onClick={() => setActiveTab('portal')} open={sidebarOpen} />
              <SidebarItem icon={<DollarSign />} label={i18n.language === 'ur' ? 'سبسکرپشن' : 'Subscription'} active={activeTab === 'finance'} onClick={() => setActiveTab('finance')} open={sidebarOpen} />
              <SidebarItem icon={<AlertTriangle />} label="Grievances" active={activeTab === 'grievances'} onClick={() => setActiveTab('grievances')} open={sidebarOpen} />
            </>
          )}
        </nav>

        {/* SOCIAL MEDIA LINKS IN SIDEBAR */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '0.75rem 0.5rem 0.25rem', flexShrink: 0, textAlign: 'center' }}>
          {sidebarOpen && (
            <div style={{ fontSize: '0.65rem', color: '#ecc94b', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Connect With Us
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: sidebarOpen ? '0.75rem' : '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <a href="https://www.facebook.com/share/1Bryh1M8AC/?mibextid=wwXIfr" target="_blank" rel="noreferrer" title="Facebook - Elum Law Associates" style={{ color: '#1877F2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.12)', transition: 'transform 0.2s' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
            </a>
            <a href="https://x.com/ElumLaw" target="_blank" rel="noreferrer" title="X (Twitter) - @ElumLaw" style={{ color: '#1DA1F2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.12)', transition: 'transform 0.2s' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path></svg>
            </a>
            <a href="https://www.linkedin.com/in/elum-law-associates-94633a428?trk=contact-info" target="_blank" rel="noreferrer" title="LinkedIn - Elum Law Associates" style={{ color: '#0A66C2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.12)', transition: 'transform 0.2s' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
            </a>
            <a href="https://www.youtube.com/@ElumLawAssociates" target="_blank" rel="noreferrer" title="YouTube - @ElumLawAssociates" style={{ color: '#FF0000', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.12)', transition: 'transform 0.2s' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
            </a>
            <a href="https://wa.me/923339389948" target="_blank" rel="noreferrer" title="WhatsApp: +923339389948" style={{ color: '#25D366', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '50%', backgroundColor: 'rgba(37,211,102,0.15)', transition: 'transform 0.2s' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
            </a>
          </div>
        </div>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '0.5rem', flexShrink: 0 }}>
          <button 
            onClick={logout}
            style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '1rem', border: 'none', background: 'none', color: '#feb2b2', cursor: 'pointer', textAlign: i18n.language === 'ur' ? 'right' : 'left', fontWeight: 'bold' }}
          >
            <LogOut style={{ marginLeft: i18n.language === 'ur' && sidebarOpen ? '1rem' : '0', marginRight: i18n.language === 'en' && sidebarOpen ? '1rem' : '0' }} />
            {sidebarOpen && <span>{t('logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, overflowY: 'auto', padding: '2rem', display: 'flex', flexDirection: 'column', backgroundColor: theme.bg, color: theme.text, transition: 'all 0.3s ease' }}>
        <div style={{ flex: 1, maxWidth: '1400px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
          {activeTab !== 'overview' && (
            <button 
              onClick={() => setActiveTab('overview')}
              style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1a365d', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
            >
              ← Back to Home
            </button>
          )}
          {renderContent()}
        </div>

        {/* DASHBOARD FOOTER WITH HIGHLY VISIBLE BRAND SOCIAL LINKS & OFFICE ADDRESS */}
        <footer style={{ 
          marginTop: '4rem', 
          padding: '2rem 0 1.5rem', 
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
          color: theme.textMuted
        }}>
          <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', justifyContent: 'center' }}>
            <a href="https://www.facebook.com/share/1Bryh1M8AC/?mibextid=wwXIfr" target="_blank" rel="noreferrer" title="Facebook - Elum Law Associates" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '50%', backgroundColor: '#1877F2', color: 'white', boxShadow: '0 4px 10px rgba(24,119,242,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
            </a>
            <a href="https://x.com/ElumLaw" target="_blank" rel="noreferrer" title="X (Twitter) - @ElumLaw" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '50%', backgroundColor: '#1DA1F2', color: 'white', boxShadow: '0 4px 10px rgba(29,161,242,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path></svg>
            </a>
            <a href="https://www.linkedin.com/in/elum-law-associates-94633a428?trk=contact-info" target="_blank" rel="noreferrer" title="LinkedIn - Elum Law Associates" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '50%', backgroundColor: '#0A66C2', color: 'white', boxShadow: '0 4px 10px rgba(10,102,194,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
            </a>
            <a href="https://www.youtube.com/@ElumLawAssociates" target="_blank" rel="noreferrer" title="YouTube - @ElumLawAssociates" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '50%', backgroundColor: '#FF0000', color: 'white', boxShadow: '0 4px 10px rgba(255,0,0,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
            </a>
            <a href="https://wa.me/923339389948" target="_blank" rel="noreferrer" title="WhatsApp Direct: +923339389948" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '50%', backgroundColor: '#25D366', color: 'white', boxShadow: '0 4px 10px rgba(37,211,102,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
            </a>
          </div>

          <div style={{ textAlign: 'center', fontSize: '0.85rem', lineHeight: '1.6' }}>
            <p style={{ margin: '0 0 0.3rem 0', fontWeight: 'bold' }}>📍 Office Address: Elum Law Associates, C-1, Times Complex, M.A Jinnah Road Quetta, Pakistan.</p>
            <p style={{ margin: 0 }}>📧 Email: <strong>elumlawassociates@gmail.com</strong> | 📞 WhatsApp / Mobile: <strong>+92 333 9389948</strong></p>
          </div>

          <p style={{ fontSize: '0.78rem', margin: 0, opacity: 0.85 }}>&copy; 2026 Elum Law Associates Pakistan. All rights reserved. Registered under domain: elumlaw.com</p>
        </footer>
      </main>
    </div>
  );
};

const SidebarItem: React.FC<{ icon: React.ReactNode, label: string, active: boolean, onClick: () => void, open: boolean }> = ({ icon, label, active, onClick, open }) => (
  <div 
    onClick={onClick}
    style={{ 
      display: 'flex', 
      alignItems: 'center', 
      padding: '1rem', 
      cursor: 'pointer', 
      backgroundColor: active ? '#2d3748' : 'transparent' 
    }}
  >
    <div style={{ marginRight: open ? '1rem' : '0' }}>{icon}</div>
    {open && <span>{label}</span>}
  </div>
);

const StatCard: React.FC<{ title: string, value: string, icon: React.ReactNode, onClick: () => void, themeCardBg: string, themeText: string }> = ({ title, value, icon, onClick, themeCardBg, themeText }) => (
  <div onClick={onClick} style={{ backgroundColor: themeCardBg, padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: themeText }}>
    <div>
      <p style={{ color: '#718096', fontSize: '0.875rem' }}>{title}</p>
      <h4 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{value}</h4>
    </div>
    {icon}
  </div>
);

const ActionButton: React.FC<{ label: string, onClick: () => void, themeText: string, themeBorder: string }> = ({ label, onClick, themeText, themeBorder }) => (
  <button 
    onClick={onClick}
    style={{ padding: '0.5rem 1rem', border: `1px solid ${themeBorder}`, borderRadius: '4px', color: themeText, backgroundColor: 'transparent', cursor: 'pointer' }}
  >
    {label}
  </button>
);

export default Dashboard;
